import {useRef, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {PageHeading} from '@/components/page-heading'
import {SectionCard} from '@/components/section-card'
import {NoAccessState} from '@/components/no-access-state'
import {DateField} from '@/components/date-field'
import {NoPsicologosNotice} from '@/components/bateria/no-psicologos-notice'
import {PsicologoSelect} from '@/components/bateria/psicologo-select'
import {CiiuPicker} from '@/components/ciiu-risk-picker'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useVerifiedPsicologos} from '@/hooks/bateria/use-bateria'
import {type CiiuCode} from '@/hooks/common/referencias/use-ciiu'
import {
  useCreateAdhoc,
  usePreviewCaracterizacion,
  type CaracterizacionEmpresaData,
  type CaracterizacionPersonaData,
} from '@/hooks/bateria/use-bateria-adhoc'
import {claveDeError, marcarErrorComoComunicado, motivoDelError, translateApiError} from '@/lib/errors'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Hint} from '@/components/ui/hint'
import {RISK_CLASS_ROMAN} from '@/lib/risk'
import {validateCedula, validateNit} from '@/lib/validation'
import i18n from '@/i18n'
import {tope} from '@/lib/campos-texto'

const NIT_YA_ES_CLIENTE = 'A tenant business with this NIT already exists'
const NIT_FIELD_ERRORS = new Set([NIT_YA_ES_CLIENTE, 'nit is required to create the business'])
const RAZON_SOCIAL_FIELD_ERRORS = new Set([
  'razon_social is required to create the business',
  'name must be at most 120 characters',
])

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

const EMPRESA_FIELDS: (keyof CaracterizacionEmpresaData)[] = [
  'razon_social',
  'nit',
  'ciiu_code',
  'correo_informe',
  'representante_legal',
  'contacto',
  'arl',
  'direccion',
  'nivel_riesgo',
  'telefono',
]

// Lista cerrada: la clase de riesgo decide la banda de diseño y quién puede certificar.
const NIVELES_RIESGO = Object.values(RISK_CLASS_ROMAN)

const PERSONA_INPUT = 'h-8 rounded-lg px-2 text-xs md:h-8'

export function AdminBateriaAdhocPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const isSystemUser = useIsSystemUser()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const preview = usePreviewCaracterizacion()
  const create = useCreateAdhoc()
  const psicologos = useVerifiedPsicologos(true)
  const razonSocialInputRef = useRef<HTMLInputElement>(null)
  const nitInputRef = useRef<HTMLInputElement>(null)

  const [fileName, setFileName] = useState<string | null>(null)
  const [empresa, setEmpresa] = useState<CaracterizacionEmpresaData | null>(null)
  const [ciiu, setCiiu] = useState<CiiuCode | null>(null)
  const [personas, setPersonas] = useState<CaracterizacionPersonaData[]>([])
  // El documento se valida al salir del campo o al enviar, nunca mientras se teclea.
  const [documentosTocados, setDocumentosTocados] = useState<number[]>([])
  const [advertencias, setAdvertencias] = useState<string[]>([])
  const [psicologoUserId, setPsicologoUserId] = useState('')
  const [fecha, setFecha] = useState(todayIso())
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [evaluacionAbierta, setEvaluacionAbierta] = useState<{
    evaluacion_id: string
    fecha_inicio: string
    personas: number
    nuevas: number
  } | null>(null)
  const [nitFieldError, setNitFieldError] = useState<string | null>(null)
  const [nitEsDeCliente, setNitEsDeCliente] = useState(false)
  const [razonSocialFieldError, setRazonSocialFieldError] = useState<string | null>(null)
  const [triedConfirm, setTriedConfirm] = useState(false)

  if (!isSystemUser) return <NoAccessState />

  function pickFile() {
    fileInputRef.current?.click()
  }

  function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setFileName(file.name)
    setConfirmError(null)
    preview.mutate(file, {
      onSuccess: (result) => {
        setEmpresa(result.empresa)
        setCiiu(
          result.empresa.ciiu_code
            ? {code: result.empresa.ciiu_code, description: result.empresa.ciiu_description ?? ''}
            : null
        )
        setPersonas(result.personas)
        setAdvertencias(result.advertencias)
        setDocumentosTocados([])
      },
    })
  }

  function updateEmpresaField(field: keyof CaracterizacionEmpresaData, value: string) {
    setEmpresa((prev) => (prev ? {...prev, [field]: value || null} : prev))
    if (field === 'nit') {
      setNitFieldError(null)
      setNitEsDeCliente(false)
    }
    if (field === 'razon_social') setRazonSocialFieldError(null)
  }

  function pickCiiu(next: CiiuCode | null) {
    setCiiu(next)
    setEmpresa((prev) =>
      prev ? {...prev, ciiu_code: next?.code ?? null, ciiu_description: next?.description ?? null} : prev
    )
  }

  function updatePersona(index: number, cambio: Partial<CaracterizacionPersonaData>) {
    setPersonas((prev) => prev.map((p, i) => (i === index ? {...p, ...cambio} : p)))
  }

  const nitValue = empresa?.nit?.trim() ?? ''
  const razonSocialValue = empresa?.razon_social?.trim() ?? ''
  const nitTypedError = !nitValue ? 'admin.bateriaAdhoc.errors.nitRequired' : (validateNit(nitValue) ?? undefined)
  const razonSocialTypedError = !razonSocialValue ? 'admin.bateriaAdhoc.errors.razonSocialRequired' : undefined
  const nitError = nitFieldError ?? (triedConfirm && nitTypedError ? i18n.t(nitTypedError) : null)
  const razonSocialError =
    razonSocialFieldError ?? (triedConfirm && razonSocialTypedError ? i18n.t(razonSocialTypedError) : null)

  const facturaCount = personas.length
  const noPersonas = personas.length === 0
  const formasFaltantes = personas.filter((p) => p.forma !== 'A' && p.forma !== 'B').length
  const documentoErrores = personas.map((p) =>
    p.documento.trim() ? validateCedula(p.documento) : 'admin.bateriaAdhoc.errors.documentoRequerido'
  )
  const documentosInvalidos = documentoErrores.filter(Boolean).length
  // Espejo de NOMBRES_REQUIRED/AREA_CARGO_SEDE_REQUIRED del backend, para avisar antes del 400.
  const nombresFaltantes = personas.filter((p) => !p.nombres.trim() || !p.apellidos.trim()).length
  const minimosFaltantes = personas.filter(
    (p) => !(p.area ?? '').trim() || !(p.cargo ?? '').trim() || !(p.sede ?? '').trim()
  ).length
  const psicologoFaltante = psicologos.length > 0 && !psicologoUserId
  const canConfirm =
    !!empresa &&
    !nitTypedError &&
    !razonSocialTypedError &&
    !noPersonas &&
    formasFaltantes === 0 &&
    documentosInvalidos === 0 &&
    nombresFaltantes === 0 &&
    minimosFaltantes === 0 &&
    !!psicologoUserId

  // El botón nunca se deshabilita: pulsarlo con faltantes los reporta todos a la vez.
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTriedConfirm(true)
    if (!empresa) return
    if (razonSocialTypedError) {
      razonSocialInputRef.current?.focus()
      return
    }
    if (nitTypedError) {
      nitInputRef.current?.focus()
      return
    }
    if (!canConfirm) return
    setConfirmError(null)
    setNitFieldError(null)
    setNitEsDeCliente(false)
    setRazonSocialFieldError(null)
    enviar({})
  }

  function enviar(salida: {agregar_a_evaluacion_id?: string; forzar_nueva?: boolean}) {
    if (!empresa) return
    setEvaluacionAbierta(null)
    create.mutate(
      {empresa, personas, psicologo_user_id: psicologoUserId, fecha_inicio: fecha, ...salida},
      {
        onSuccess: (result) =>
          navigate(`/admin/bateria/empresas/${result.business_id}/evaluaciones/${result.evaluacion.id}`),
        onError: (err) => {
          const motivo = motivoDelError(err)
          if (motivo?.motivo === 'evaluacion_abierta') {
            marcarErrorComoComunicado(err)
            setEvaluacionAbierta(motivo as unknown as NonNullable<typeof evaluacionAbierta>)
            return
          }
          const detail = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail
          if (typeof detail === 'string' && NIT_FIELD_ERRORS.has(detail)) {
            marcarErrorComoComunicado(err)
            setNitFieldError(translateApiError(err))
            setNitEsDeCliente(detail === NIT_YA_ES_CLIENTE)
            return
          }
          if (typeof detail === 'string' && RAZON_SOCIAL_FIELD_ERRORS.has(detail)) {
            marcarErrorComoComunicado(err)
            setRazonSocialFieldError(translateApiError(err))
            return
          }
          // Sin `translateApiError`: marcaría cualquier error, inesperado incluido, y la red global dejaría de reportarlo.
          setConfirmError(i18n.t(claveDeError(err)))
        },
      }
    )
  }

  return (
    <div className="space-y-6" data-testid="admin-bateria-adhoc">
      <div className="space-y-2">
        <Link
          to="/admin/bateria"
          className="font-mono text-[11px] text-primary hover:underline"
          data-testid="adhoc-back">
          {t('admin.bateria.detail.back')}
        </Link>
        <PageHeading title={t('admin.bateriaAdhoc.title')} />
      </div>

      {!empresa && (
        <SectionCard
          className="max-w-xl"
          title={t('admin.bateriaAdhoc.uploadTitle')}
          bodyClassName="space-y-3 px-5 py-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            data-testid="adhoc-file-input"
            onChange={onFileSelected}
          />
          <Button onClick={pickFile} disabled={preview.isPending} data-testid="adhoc-pick-file">
            {preview.isPending ? t('admin.bateriaAdhoc.parsing') : t('admin.bateriaAdhoc.selectFile')}
          </Button>
          {fileName && <p className="font-mono text-xs text-muted-foreground">{fileName}</p>}
          {preview.isError && (
            <p className="text-sm text-destructive" data-testid="adhoc-preview-error">
              {translateApiError(preview.error)}
            </p>
          )}
        </SectionCard>
      )}

      {empresa && (
        <form className="space-y-6" onSubmit={handleSubmit} noValidate>
          {advertencias.length > 0 && (
            <div className="notice-amber rounded-lg px-4 py-3 text-sm" data-testid="adhoc-advertencias">
              <p className="font-medium">{t('admin.bateriaAdhoc.advertenciasTitle')}</p>
              <ul className="mt-2 list-inside list-disc space-y-1 opacity-90">
                {advertencias.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          <SectionCard title={t('admin.bateriaAdhoc.empresaTitle')} bodyClassName="px-5 py-4">
            <div className="grid grid-cols-1 gap-4">
              {EMPRESA_FIELDS.map((field) => {
                const fieldError = field === 'nit' ? nitError : field === 'razon_social' ? razonSocialError : null
                const fieldRef =
                  field === 'nit' ? nitInputRef : field === 'razon_social' ? razonSocialInputRef : undefined
                return (
                  <div key={field} className="space-y-2">
                    <Label htmlFor={field === 'ciiu_code' ? 'ciiu_trigger' : `adhoc-empresa-${field}`}>
                      {t(`admin.bateriaAdhoc.empresaFields.${field}`)}
                      {(field === 'nit' || field === 'razon_social') && ' *'}
                    </Label>
                    {field === 'ciiu_code' ? (
                      <CiiuPicker ciiu={ciiu} onChange={pickCiiu} />
                    ) : field === 'nivel_riesgo' ? (
                      <select
                        id="adhoc-empresa-nivel_riesgo"
                        data-testid="adhoc-empresa-nivel_riesgo"
                        value={empresa.nivel_riesgo ?? ''}
                        onChange={(e) => updateEmpresaField('nivel_riesgo', e.target.value)}
                        className="h-11 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground md:h-9">
                        <option value="">{t('admin.bateriaAdhoc.nivelRiesgoPlaceholder')}</option>
                        {NIVELES_RIESGO.map((nivel) => (
                          <option key={nivel} value={nivel}>
                            {t('business.risk.classValue', {roman: nivel})}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        maxLength={tope('NOTA')}
                        ref={fieldRef}
                        id={`adhoc-empresa-${field}`}
                        data-testid={`adhoc-empresa-${field}`}
                        value={empresa[field] ?? ''}
                        onChange={(e) => updateEmpresaField(field, e.target.value)}
                        aria-invalid={!!fieldError}
                      />
                    )}
                    {fieldError && (
                      <p className="text-xs text-destructive" data-testid={`adhoc-empresa-${field}-error`}>
                        {fieldError}
                      </p>
                    )}
                    {/* El «Nueva batería» de la mesa de trabajo busca esa empresa por NIT. */}
                    {field === 'nit' && nitEsDeCliente && (
                      <Link
                        to="/admin/bateria"
                        className="inline-block text-xs text-primary hover:underline"
                        data-testid="adhoc-nit-salida">
                        {t('admin.bateriaAdhoc.errors.nitYaEsClienteSalida')}
                      </Link>
                    )}
                  </div>
                )
              })}
            </div>
          </SectionCard>

          <SectionCard
            title={t('admin.bateriaAdhoc.personalTitle')}
            actions={
              <span className="font-mono text-xs tabular-nums text-muted-foreground">
                {t('admin.bateriaAdhoc.personalCount', {count: facturaCount})}
              </span>
            }
            bodyClassName="space-y-3 px-2 py-2">
            {triedConfirm && noPersonas && (
              <p className="px-3 text-sm text-destructive" data-testid="adhoc-no-personas">
                {t('admin.bateriaAdhoc.errors.noPersonas')}
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="surface-divider border-b bg-muted/55 text-left font-mono text-[11px] tracking-[0.12em] text-muted-foreground uppercase">
                    <th className="py-2.5 pr-2 pl-3">{t('admin.bateriaAdhoc.table.documento')}</th>
                    <th className="py-2.5 pr-2">{t('admin.bateriaAdhoc.table.nombres')}</th>
                    <th className="py-2.5 pr-2">{t('admin.bateriaAdhoc.table.apellidos')}</th>
                    <th className="py-2.5 pr-2">{t('admin.bateriaAdhoc.table.area')}</th>
                    <th className="py-2.5 pr-2">{t('admin.bateriaAdhoc.table.cargo')}</th>
                    <th className="py-2.5 pr-2">{t('admin.bateriaAdhoc.table.sede')}</th>
                    <th className="py-2.5 pr-2">{t('admin.bateriaAdhoc.table.forma')}</th>
                  </tr>
                </thead>
                <tbody>
                  {personas.map((p, i) => {
                    const documentoError =
                      (triedConfirm || documentosTocados.includes(i)) && documentoErrores[i]
                        ? i18n.t(documentoErrores[i] as string)
                        : null
                    return (
                      <tr key={i} className="surface-divider border-b align-top last:border-0 hover:bg-muted/40">
                        <td className="py-1.5 pr-2 pl-3">
                          <Input
                            maxLength={tope('DOC')}
                            data-testid={`adhoc-persona-documento-${i}`}
                            aria-label={t('admin.bateriaAdhoc.table.documento')}
                            value={p.documento}
                            onChange={(e) => updatePersona(i, {documento: e.target.value})}
                            onBlur={() => setDocumentosTocados((prev) => (prev.includes(i) ? prev : [...prev, i]))}
                            aria-invalid={!!documentoError}
                            className={`w-32 font-mono ${PERSONA_INPUT}`}
                          />
                          {documentoError && (
                            <p
                              className="mt-1 text-[11px] text-destructive"
                              data-testid={`adhoc-persona-documento-${i}-error`}>
                              {documentoError}
                            </p>
                          )}
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input
                            maxLength={tope('NOM')}
                            data-testid={`adhoc-persona-nombres-${i}`}
                            aria-label={t('admin.bateriaAdhoc.table.nombres')}
                            value={p.nombres}
                            onChange={(e) => updatePersona(i, {nombres: e.target.value})}
                            className={`w-40 ${PERSONA_INPUT}`}
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input
                            maxLength={tope('NOM')}
                            data-testid={`adhoc-persona-apellidos-${i}`}
                            aria-label={t('admin.bateriaAdhoc.table.apellidos')}
                            value={p.apellidos}
                            onChange={(e) => updatePersona(i, {apellidos: e.target.value})}
                            className={`w-40 ${PERSONA_INPUT}`}
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input
                            maxLength={tope('ORG')}
                            data-testid={`adhoc-persona-area-${i}`}
                            aria-label={t('admin.bateriaAdhoc.table.area')}
                            value={p.area ?? ''}
                            onChange={(e) => updatePersona(i, {area: e.target.value || null})}
                            className={`w-40 ${PERSONA_INPUT}`}
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input
                            maxLength={tope('CARGO')}
                            data-testid={`adhoc-persona-cargo-${i}`}
                            aria-label={t('admin.bateriaAdhoc.table.cargo')}
                            value={p.cargo ?? ''}
                            onChange={(e) => updatePersona(i, {cargo: e.target.value || null})}
                            className={`w-48 ${PERSONA_INPUT}`}
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input
                            maxLength={tope('ORG')}
                            data-testid={`adhoc-persona-sede-${i}`}
                            aria-label={t('admin.bateriaAdhoc.table.sede')}
                            value={p.sede ?? ''}
                            onChange={(e) => updatePersona(i, {sede: e.target.value || null})}
                            className={`w-36 ${PERSONA_INPUT}`}
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <select
                            data-testid={`adhoc-forma-${i}`}
                            aria-label={t('admin.bateriaAdhoc.table.forma')}
                            value={p.forma ?? ''}
                            onChange={(e) => updatePersona(i, {forma: e.target.value})}
                            className="h-8 rounded-lg border border-input bg-transparent px-2 text-xs text-foreground">
                            <option value="">{t('admin.bateriaAdhoc.table.formaPlaceholder')}</option>
                            <option value="A">A</option>
                            <option value="B">B</option>
                          </select>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>

          <SectionCard title={t('admin.bateriaAdhoc.aplicacionTitle')} bodyClassName="space-y-4 px-5 py-4">
            <div className="space-y-2">
              <Label htmlFor="adhoc-psicologo">{t('admin.bateriaAdhoc.psicologo')}</Label>
              {psicologos.length === 0 ? (
                <NoPsicologosNotice testId="adhoc-no-psicologos" />
              ) : (
                <div className="max-w-sm">
                  <PsicologoSelect
                    id="adhoc-psicologo"
                    psicologos={psicologos}
                    value={psicologoUserId}
                    onChange={setPsicologoUserId}
                    testId="adhoc-psicologo"
                  />
                </div>
              )}
              {triedConfirm && psicologoFaltante && (
                <p className="text-xs text-destructive" data-testid="adhoc-psicologo-required">
                  {t('admin.bateria.nuevaDialog.psicologoRequired')}
                </p>
              )}
            </div>
            <DateField
              id="adhoc-fecha"
              label={t('admin.bateria.nuevaDialog.fecha')}
              value={fecha}
              max={todayIso()}
              onChange={setFecha}
            />

            {(formasFaltantes > 0 || documentosInvalidos > 0 || nombresFaltantes > 0 || minimosFaltantes > 0) && (
              <div className="notice-amber space-y-0.5 rounded-lg px-4 py-2.5 text-sm">
                {formasFaltantes > 0 && (
                  <p data-testid="adhoc-formas-faltantes">
                    {t('admin.bateriaAdhoc.formasFaltantes', {count: formasFaltantes})}
                  </p>
                )}
                {documentosInvalidos > 0 && (
                  <p data-testid="adhoc-documentos-invalidos">
                    {t('admin.bateriaAdhoc.documentosInvalidos', {count: documentosInvalidos})}
                  </p>
                )}
                {nombresFaltantes > 0 && (
                  <p data-testid="adhoc-nombres-faltantes">
                    {t('admin.bateriaAdhoc.nombresFaltantes', {count: nombresFaltantes})}
                  </p>
                )}
                {minimosFaltantes > 0 && (
                  <p data-testid="adhoc-minimos-faltantes">
                    {t('admin.bateriaAdhoc.minimosFaltantes', {count: minimosFaltantes})}
                  </p>
                )}
              </div>
            )}
            {confirmError && (
              <p className="text-sm text-destructive" data-testid="adhoc-confirm-error">
                {confirmError}
              </p>
            )}
            <Dialog open={evaluacionAbierta !== null} onOpenChange={(open) => !open && setEvaluacionAbierta(null)}>
              <DialogContent data-testid="adhoc-evaluacion-abierta">
                <DialogHeader>
                  <DialogTitle>{t('admin.bateriaAdhoc.evaluacionAbierta.title')}</DialogTitle>
                  <DialogDescription>
                    {evaluacionAbierta &&
                      t('admin.bateriaAdhoc.evaluacionAbierta.description', {
                        fecha: evaluacionAbierta.fecha_inicio,
                        personas: evaluacionAbierta.personas,
                        nuevas: evaluacionAbierta.nuevas,
                      })}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch">
                  <Button
                    type="button"
                    data-testid="adhoc-agregar-a-existente"
                    disabled={create.isPending || (evaluacionAbierta?.nuevas ?? 0) === 0}
                    onClick={() =>
                      evaluacionAbierta && enviar({agregar_a_evaluacion_id: evaluacionAbierta.evaluacion_id})
                    }>
                    {t('admin.bateriaAdhoc.evaluacionAbierta.agregar', {count: evaluacionAbierta?.nuevas ?? 0})}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="adhoc-forzar-nueva"
                    disabled={create.isPending}
                    onClick={() => enviar({forzar_nueva: true})}>
                    {t('admin.bateriaAdhoc.evaluacionAbierta.crearOtra')}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setEvaluacionAbierta(null)}>
                    {t('common.cancel')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <div className="surface-divider space-y-1.5 border-t pt-4">
              <Button type="submit" disabled={create.isPending} data-testid="adhoc-confirm">
                {create.isPending ? t('admin.bateriaAdhoc.confirming') : t('admin.bateriaAdhoc.confirm')}
              </Button>
              <Hint testId="adhoc-confirm-hint">{t('admin.bateriaAdhoc.aplicacionHint')}</Hint>
            </div>
          </SectionCard>
        </form>
      )}
    </div>
  )
}
